import useTranslation from '@/providers/I18n/hooks/useTranslation';
import React, { useEffect, useRef, useState } from 'react';
import Input from '@/components/atoms/input';
import Button from '@/components/atoms/button';
import SelectField from '@/components/atoms/floating-select';
import Spinner from '../../spinner';
import { sdk } from '@/sdk/CommercetoolsSDK';
import { useParams, useSearchParams } from 'next/navigation';
import ReCAPTCHA from 'react-google-recaptcha';
import Checkbox from '@/components/atoms/checkbox';
import { RECAPTCHA_SITE_KEY } from '@/helpers/constants/googleAnalytics';

interface ContactFormData {
  Name: string;
  Email: string;
  Phone_Number: string | number;
  Message: string;
  FileName: string;
  AttachmentType: string;
  DocumentBase64: any;
  PreferredContactMethod: string;
  PrivacyConsent: string;
  CaptuchaVerification: boolean;
  Country: string;
  PurposeofEnquiry?: string;
}
export type ContactProps = {
  account: any;
};

const EnquiryForm: React.FC<ContactProps> = ({ account }) => {
  const userAccount = account?.account;
  const { locale } = useParams();

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [clearDropdowns, setClearDropdowns] = useState(false);
  const searchParams = useSearchParams();
  const purpose = searchParams.get('purpose') || '';
  const [errorMessage, setErrorMesage] = useState('');
  const [fileErrorMessage, setFileErrorMesage] = useState('');
  const [success, setSuccess] = useState('');
  const { translate } = useTranslation();
  const [loading, setloading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const extensions = sdk.composableCommerce;
  const [purposeofEnquireError, setpurposeofEnquireError] = useState('');
  const [nameError, setNameError] = useState('');
  const [reasonforContactError, setReasonforContactError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [invalidPhoneNumError, setInvalidPhoneNumError] = useState('');
  const [reCaptchaError, setReCaptchaError] = useState('');
  const [formData, setFormData] = useState<ContactFormData>({
    Name: '',
    Email: '',
    Phone_Number: '',
    Message: '',
    FileName: '',
    AttachmentType: '',
    DocumentBase64: '',
    PreferredContactMethod: '',
    PrivacyConsent: '',
    Country: '',
    CaptuchaVerification: false,
    PurposeofEnquiry: '',
  });

  const purposeRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  const [touched, setTouched] = useState<Record<keyof FormData, boolean>>(
    Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: false }), {} as Record<keyof FormData, boolean>),
  );

  const resetField = {
    Phone_Number: '',
    Message: '',
    FileName: '',
    AttachmentType: '',
    DocumentBase64: '',
    PreferredContactMethod: '',
    Country: '',
    PrivacyConsent: '',
  };
  const onChangeRecaptcha = (e: any) => {
    console.log('Captcha value:', e);
    if (e) {
      setReCaptchaError('');
      setErrorMesage('');
      setFormData((prev) => ({ ...prev, CaptuchaVerification: true }));
    } else {
      setReCaptchaError('reCAPTCHA verification failed. Please try again.');
      setFormData((prev) => ({ ...prev, CaptuchaVerification: false }));
    }
  };

  const SubTypes = [
    { label: 'Phone', value: 'Phone' },
    { label: 'Email', value: 'Email' },
  ];
  const FormType = [
    { label: 'General Enquiry', value: 'General Enquiry' },
    { label: 'Report an Issue', value: 'Report an Issue' },
    { label: 'Technical Enquiry', value: 'Technical Enquiry' },
  ];

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const captchaValue = recaptchaRef.current?.getValue();
    if (!handleContactUsForm()) {
      return;
    }
    if (!captchaValue) {
      setErrorMesage('reCaptcha verification failed. Please try again');
      return;
    }
    await handleAPICall(e);
  };

  const handleAPICall = async (e: any) => {
    setloading(true);

    const data = {
      First_Name: formData.Name,
      Purpose_of_Enquiry: formData.PurposeofEnquiry,
      Email: formData.Email,
      Preferred_Contact_Method: formData.PreferredContactMethod,
      Country: formData.Country,
      Phone: formData.Phone_Number,
      Message: formData.Message,
      FileName: formData.FileName,
      AttachmentType: formData.AttachmentType,
      DocumentBase64: formData.DocumentBase64,
      Source_Site: 'PlumbersHub',
      Consent: formData.PrivacyConsent,
    };
    try {
      const res = await extensions.custom.submitContactUs(data);
      const isSuccess = res?.isError === false;
      const message = isSuccess ? 'Your query has been submitted.' : 'An error has occurred. Please try again later.';

      let updatedFields: Record<string, string> = { ...resetField };

      if (!userAccount) {
        updatedFields = { ...updatedFields, Name: '', Email: '', PurposeofEnquiry: '' };
      } else if (purpose && userAccount) {
        updatedFields = { ...updatedFields };
      } else if (userAccount) {
        updatedFields = { ...updatedFields, PurposeofEnquiry: '' };
      }

      setFormData((prev) => ({ ...prev, ...updatedFields }));

      if (isSuccess) {
        setSuccess(message);
        setClearDropdowns(true);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setErrorMesage(message);
        setTimeout(() => setErrorMesage(''), 3000);
      }
    } catch (error) {
      setErrorMesage('An error has occurred. Please try again later.');
      setTimeout(() => {
        setErrorMesage('');
      }, 3000);
    } finally {
      setloading(false);
        recaptchaRef.current?.reset();
        setFormData((prev) => ({
    ...prev,
    CaptuchaVerification: false,
  }));
    }
  };

  const updateData = (name: string, value: any) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setClearDropdowns(false);
    const { name, type, value } = e.target as HTMLInputElement;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    if (name === 'Phone_Number') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length > 12) return;
      if (invalidPhoneNumError) setInvalidPhoneNumError('');
      updateData(name, numericValue);
      return;
    }

    const newValue = type === 'checkbox' ? (name === 'PrivacyConsent' ? (checked ? 'true' : 'false') : checked) : value;
    if (name === 'Email' && emailError) setEmailError('');
    if (name === 'Name' && nameError) setNameError('');
    if (name === 'Message' && reasonforContactError) setReasonforContactError('');
    if (name === 'PrivacyConsent' && errorMessage.includes('privacy')) setErrorMesage('');

    updateData(name, newValue);
  };

  const handlePreferredContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    updateData('PreferredContactMethod', e.target.value.toString());
  };

  const handleFormType = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    updateData('PurposeofEnquiry', e.target.value.toString());
    if (purposeofEnquireError) setpurposeofEnquireError('');
  };

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.split('.').pop();
      
      const allowedExtensions = ['pdf', 'jpeg', 'png'];
      const allowedMimeTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      
      const isValidExtension = allowedExtensions.includes(fileExtension || '');
      
      const isValidMimeType = allowedMimeTypes.includes(file.type);
      
      if (!isValidExtension || !isValidMimeType) {
        setFileErrorMesage('Only PDF, JPEG, and PNG files are allowed.');
        setFormData((prev) => {
          return {
            ...prev,
            FileName: '',
            AttachmentType: '',
            DocumentBase64: '',
          };
        });
        setFileInputKey((prev) => prev + 1);
        return;
      }
      
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > 5) {
        setFileErrorMesage('File exceeds 5 mb');
      } else {
      setFileErrorMesage('');
      }
      const name = file?.name.split('.')[0];
      const type = file?.type.split('/')[1];
      const reader = new FileReader();
      reader.onloadend = () => {
        const data = reader as any;
        const base64 = data.result.split(',')[1];
        setFormData((prev) => {
          return {
            ...prev,
            FileName: name,
            AttachmentType: type,
            DocumentBase64: base64,
          };
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleContactUsForm = () => {
    if (!formData.PurposeofEnquiry) {
      setpurposeofEnquireError('Purpose of enquiry is required.');
      purposeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      purposeRef.current?.focus();
      return false;
    }
    if (!formData.Name) {
      setNameError('Name is required.');
      nameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      nameRef.current?.focus();
      return false;
    }

    if (!formData.Email) {
      setEmailError('Email is required');
      emailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      emailRef.current?.focus();
      return false;
    }

    if (formData.Email) {
      const emailPattern =
        /^([a-zA-Z0-9_\-\.]+(\+[a-zA-Z0-9_\-\.]+)?)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/;
      if (!emailPattern.test(formData.Email)) {
        setEmailError('Please enter a valid email address.');
        emailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        emailRef.current?.focus();
        return false;
      }
    }
    if (!formData.Message) {
      setReasonforContactError('Reason for contact is required.');
      messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      messageRef.current?.focus();
      return false;
    }

    if (fileErrorMessage) {
      return false;
    }

    if (formData.PrivacyConsent !== 'true') {
      setErrorMesage('Please accept the privacy consent.');
      return false;
    }
    setErrorMesage('');
    return true;
  };

  useEffect(() => {
    if (userAccount) {
      setFormData({
        ...formData,
        Name: `${userAccount.firstName ?? ''} ${userAccount.lastName ?? ''}`.trim(),
        Email: userAccount?.email ?? '',
        Country: userAccount?.country?.toUpperCase() ?? '',
      });
    } else {
      setFormData({
        ...formData,
        Country: locale?.toUpperCase(),
      });
    }
  }, []);

  useEffect(() => {
    if (purpose) {
      setFormData((prev) => {
        return {
          ...prev,
          PurposeofEnquiry: 'Report an Issue',
        };
      });
    }
  }, [purpose]);

  const handleBlur = (field: keyof ContactFormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  return (
    <>
      {loading && <Spinner />}
      {loading && <div className="overlay fixed left-0 top-0 z-[1002] h-full w-full bg-gray-800 opacity-75"></div>}
      <form onSubmit={handleFormSubmit}>
        <div className="leading-15 flex flex-1 flex-col gap-4">
          <div ref={purposeRef} className="flex flex-col gap-1 [&>div>div]:mb-0">
            <span className="text-secondary-black text-16 font-medium">
              {'Purpose of Enquiry '} <span className="!text-red-500"> * </span>
            </span>
            <SelectField
              label="Purpose of Enquiry"
              name="Purpose of Enquiry"
              value={formData?.PurposeofEnquiry ?? ''}
              options={FormType}
              onChange={handleFormType}
              onBlur={() => handleBlur('PurposeofEnquiry')}
              hidelabel={true}
            />
            {purposeofEnquireError && (
              <div>
                <span className="text-14 text-red-600">{purposeofEnquireError}</span>
              </div>
            )}
          </div>
          <div ref={nameRef} className="flex flex-col gap-1 [&>div>div]:mb-0">
            <span className="text-secondary-black text-16 font-medium">
              {'Name '} <span className="!text-red-500"> * </span>
            </span>
            <Input
              type="text"
              name="Name"
              maxLength={50}
              onChange={handleChange}
              value={formData?.Name || ''}
              placeholder="Name"
              autoComplete=""
              className="!text-14 placeholder:text-[#666666]"
              containerClassName="!rounded-sm !border-1  !border-[#747474]"
            />
            {nameError && (
              <div>
                <span className="text-14 text-red-600">{nameError}</span>
              </div>
            )}
          </div>

          <div ref={emailRef} className="flex flex-col gap-1 [&>div>div]:mb-0">
            <span className="text-secondary-black text-16 font-medium">
              {'Email '} <span className="!text-red-500"> * </span>
            </span>
            <Input
              name="Email"
              title="Please enter a valid email address."
              type="text"
              value={formData?.Email}
              onChange={handleChange}
              placeholder="Email Address *"
              autoComplete=""
              maxLength={100}
              className="!text-14 placeholder:text-[#666666]"
              containerClassName="!rounded-sm !border-1  !border-[#747474]"
            />
            {emailError && (
              <div>
                <span className="text-14 text-red-600">{emailError}</span>
              </div>
            )}
          </div>

          <div ref={phoneRef} className="flex flex-col gap-1 [&>div>div]:mb-0">
            <span className="text-secondary-black text-16 font-medium">{'Phone '}</span>
            <Input
              type="tel"
              name="Phone_Number"
              onChange={handleChange}
              placeholder="Phone"
              value={formData?.Phone_Number}
              autoComplete=""
              className="!text-14 placeholder:text-[#666666]"
              containerClassName="!rounded-sm !border-1  !border-[#747474]"
            />
            {invalidPhoneNumError && (
              <div>
                <span className="text-14 text-red-600">{invalidPhoneNumError}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1 [&>div>div]:mb-0">
            <span className="text-secondary-black text-16 font-medium">{'Preferred Contact Method '}</span>
            <SelectField
              name="Preferred Contact Method"
              label="Preferred Contact Method"
              value={formData?.PreferredContactMethod}
              options={SubTypes}
              onChange={handlePreferredContactChange}
              onBlur={() => handleBlur('PreferredContactMethod')}
              hidelabel={true}
            />
          </div>

          <div className="flex flex-col gap-1 [&>div>div]:mb-0">
            <span className={`text-secondary-black text-16 font-medium tracking-wide`}>Chose a file to upload</span>
            {!loading ? (
              <Input
                key={fileInputKey}
                ref={fileInputRef}
                type="file"
                name="files"
                onChange={handleFileChange}
                className="!text-14 placeholder:text-[#666666]"
                containerClassName="!rounded-sm !border-1  !border-[#747474] py-0.5"
                accept=".pdf,.png,.jpeg,"
              />
            ) : (
              <></>
            )}
            {fileErrorMessage && (
              <>
                <span className="text-14 text-red-600">{fileErrorMessage}</span> <br />
              </>
            )}

            <span className="pt-1 text-sm text-[#666666]">
              {'PDF, JPEG and PNG only'}
            </span>
          </div>

          <div ref={messageRef} className="flex flex-col gap-1 [&>div>div]:mb-0">
            <span className="text-secondary-black text-16 font-medium">
              {'Reason for Contact '} <span className="!text-red-500"> * </span>
            </span>
            <textarea
              name="Message"
              onChange={handleChange}
              placeholder="Describe your query here ... (Max 300 characters) *"
              value={formData?.Message}
              maxLength={300}
              autoComplete=""
              style={{ boxShadow: 'none' }}
              className="border-1 h-100 !focus:outline-none !focus:border-black w-full !rounded-sm !border-[#747474] pb-10 text-left !text-14 placeholder:text-[#666666]"
            />
            {reasonforContactError && (
              <div>
                <span className="text-14 text-red-600">{reasonforContactError}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1 [&>div>div]:mb-0">
            <span className={`text-secondary-black text-16 font-medium tracking-wide`}>
              Privacy consent
              <span className="!text-red-500"> * </span>
            </span>
            <div className="flex items-start gap-4">
              <Checkbox
                name="PrivacyConsent"
                checked={formData.PrivacyConsent === 'true'}
                onChange={handleChange}
                label={
                  <>
                    By submitting this form you are providing your personal information to GWA for use in accordance
                    with our collection statement. GWA will use all personal information in accordance with the GWA
                    Group Limited Privacy Policy.
                  </>
                }
                className={
                  formData.PrivacyConsent === 'true'
                    ? 'font-noraml !border-black bg-black text-16 !text-black'
                    : 'font-noraml !border-black !bg-white text-16 !text-black'
                }
              />
            </div>
          </div>

          <ReCAPTCHA ref={recaptchaRef} sitekey={`${RECAPTCHA_SITE_KEY}`} onChange={onChangeRecaptcha} />
          {success ? (
            <div>
              <span className="text-14 text-green-600">{success}</span>
            </div>
          ) : (
            <div>
              <span className="text-14 text-red-600">{errorMessage}</span>
            </div>
          )}
          <Button
            size="s"
            type="submit"
            disabled={loading}
            className={`w-full rounded-none ${
              loading ? '!bg-gray-400 !text-white' : '!bg-gray-700'
            } cursor-pointer py-5 text-center text-base font-medium leading-6 md:w-[177px]`}
          >
            Submit
          </Button>
        </div>
      </form>
    </>
  );
};

export default EnquiryForm;
